import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { getErrorMessage } from '@/lib/api-errors'
import { toast } from '@/lib/toast'
import {
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/shared/api/notificationsApi'
import { useAuthStore } from '@/shared/auth/stores/authStore'

import type {
  ListNotificationsParams,
  NotificationsResponse,
} from '@/shared/api/notificationsApi'

// ── Polling cadence ────────────────────────────────────────────────────────
// Per MOBILE_API_SPEC §6.4 + the design tokens (mobile-design-tokens.json):
// foreground 30s for unread-count, on-bell-tap for fresh list.
const UNREAD_POLL_MS = 30 * 1000
const LIST_STALE_MS = 30 * 1000

// ── List feed ──────────────────────────────────────────────────────────────

export function notificationsQueryOptions(params: ListNotificationsParams = {}) {
  return queryOptions({
    queryKey: ['notifications', params] as const,
    queryFn: () => listNotifications(params),
    placeholderData: keepPreviousData,
    staleTime: LIST_STALE_MS,
  })
}

export function useNotifications(params?: ListNotificationsParams) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  return useQuery({
    ...notificationsQueryOptions(params ?? {}),
    enabled: isAuthenticated,
  })
}

// ── Unread count (badge) ───────────────────────────────────────────────────
// Polled every 30s while authenticated. Used by the bell-icon badge in
// app-header so the count stays fresh without re-fetching the full feed.

export function useUnreadNotificationCount() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  return useQuery({
    queryKey: ['notifications', 'unread-count'] as const,
    queryFn: () => getUnreadCount(),
    enabled: isAuthenticated,
    refetchInterval: UNREAD_POLL_MS,
    refetchIntervalInBackground: false, // pause when tab/window hidden
    staleTime: UNREAD_POLL_MS / 2,
  })
}

// ── Mark single read ───────────────────────────────────────────────────────
// Optimistic — flip read_at locally + decrement badge before the server
// responds. If the request fails, revert.

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] })

      // Snapshot every cached notifications-list query so we can roll back.
      const previousLists = queryClient.getQueriesData<NotificationsResponse>({
        queryKey: ['notifications'],
      })

      const nowIso = new Date().toISOString()

      // Update every list query that contains this notification.
      previousLists.forEach(([key, data]) => {
        if (!data) {return}
        const item = data.items.find((n) => n.id === id)
        if (!item || item.read_at) {return}
        queryClient.setQueryData<NotificationsResponse>(key, {
          ...data,
          items: data.items.map((n) =>
            n.id === id ? { ...n, read_at: nowIso } : n,
          ),
          unread_count: Math.max(0, (data.unread_count ?? 1) - 1),
        })
      })

      // Optimistically drop the standalone unread count by 1.
      const previousUnread = queryClient.getQueryData<{ unread_count: number }>([
        'notifications',
        'unread-count',
      ])
      if (previousUnread) {
        queryClient.setQueryData(['notifications', 'unread-count'], {
          unread_count: Math.max(0, previousUnread.unread_count - 1),
        })
      }

      return { previousLists, previousUnread }
    },
    onError: (_err, _id, ctx) => {
      // Roll back optimistic updates on failure.
      ctx?.previousLists.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
      if (ctx?.previousUnread) {
        queryClient.setQueryData(['notifications', 'unread-count'], ctx.previousUnread)
      }
    },
    onSettled: () => {
      // Quietly resync to canonical server state.
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    },
  })
}

// ── Mark all read ──────────────────────────────────────────────────────────

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: (res) => {
      // Optimistically clear unread state across every cached list.
      const nowIso = new Date().toISOString()
      queryClient.setQueriesData<NotificationsResponse>(
        { queryKey: ['notifications'] },
        (old) => {
          if (!old) {return old}
          return {
            ...old,
            items: old.items.map((n) => ({ ...n, read_at: n.read_at ?? nowIso })),
            unread_count: 0,
          }
        },
      )
      queryClient.setQueryData(['notifications', 'unread-count'], { unread_count: 0 })
      toast.success(
        res.marked_count > 0
          ? `${res.marked_count} notification${res.marked_count === 1 ? '' : 's'} marked as read.`
          : 'Already up to date.',
      )
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, 'Could not mark all as read.'))
    },
  })
}
